import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { BanknoteIcon, CircleDotIcon, HandCoinsIcon, HourglassIcon, PackageIcon, StoreIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { membershipFor, useMe } from '@/api/me'
import { useSalesChannels } from '@/api/sales-channels'
import { useSalesReports } from '@/api/sales-reports'
import type { SalesReport } from '@/api/sales-reports'
import { VoidedBadge } from '@/components/corrections/voided'
import { CsvExportButton } from '@/components/csv-export/csv-export-button'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { StatusBadge } from '@/components/sales-reports/status-badge'
import { Button } from '@/components/ui/button'

// Sales Reports list (UI-SCREENS S-05, 06b #185): channel, period, units, expected, settlement,
// paid, outstanding and the derived status badge, filterable by channel, date range and status.
// Owner and Staff both see it; Staff see the units only — the server omits every revenue figure,
// so the money columns simply aren't there for them (permissions.md, UI-SCREENS G2). The filters
// sit in the shared filter bar (as on I-03) and live in the URL, so a filtered list survives a
// reload and can be linked to.
const STATUSES = ['unpaid', 'partially_paid', 'paid', 'voided'] as const

const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional().catch(undefined),
  status: z.enum(STATUSES).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/sales-reports/')({
  component: SalesReportsList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'salesReports.title' },
})

function SalesReportsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const me = useMe()
  const isOwner = membershipFor(me.data, slug)?.role === 'owner'
  const channels = useSalesChannels(slug)
  const filters = { salesChannelId: search.channelId, from: search.from, to: search.to, status: search.status }
  const reports = useSalesReports(slug, filters)
  const filtered = Object.values(filters).some((value) => value !== undefined)
  // Two CSV files for this screen (#187, G33): one row per report line, and the payment rows of
  // the same reports. Both take the filters on screen, so they hold every page of the list — and
  // both are Owner only (permissions.md): hidden for Staff, and the endpoints refuse them.
  const exportQuery = {
    sales_channel_id: filters.salesChannelId,
    from: filters.from,
    to: filters.to,
    status: filters.status,
  }

  function set(patch: Partial<typeof search>) {
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('salesReports.title')}</h1>
        {isOwner ? (
          <div className="flex flex-wrap gap-2">
            <CsvExportButton slug={slug} list="sales_reports" query={exportQuery} label={t('exports.salesReports')} />
            <CsvExportButton slug={slug} list="payments" query={exportQuery} label={t('exports.payments')} />
            <Button asChild className="h-11">
              <Link to="/w/$slug/sales-reports/new" params={{ slug }}>
                {t('salesReports.new')}
              </Link>
            </Button>
          </div>
        ) : null}
      </div>

      <FilterBar
        fields={[
          {
            key: 'channelId',
            label: t('salesReports.columns.channel'),
            icon: StoreIcon,
            value: search.channelId ? String(search.channelId) : undefined,
            options: (channels.data ?? []).map((channel) => ({ value: String(channel.id), label: channel.name })),
            onChange: (value) => set({ channelId: value === undefined ? undefined : Number(value) }),
          },
          {
            key: 'status',
            label: t('salesReports.columns.status'),
            icon: CircleDotIcon,
            value: search.status,
            options: STATUSES.map((value) => ({ value, label: t(`salesReports.statuses.${value}`) })),
            onChange: (value) => set({ status: value as (typeof STATUSES)[number] | undefined }),
          },
        ]}
        date={{ from: search.from, to: search.to }}
        onDateChange={(range) => set({ from: range.from, to: range.to })}
        onClearAll={() => set({ channelId: undefined, status: undefined, from: undefined, to: undefined })}
      />

      {reports.isPending ? (
        <LoadingSkeleton />
      ) : reports.isError || !reports.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : reports.data.length === 0 ? (
        <RecordListEmpty>{t(filtered ? 'salesReports.emptyFiltered' : 'salesReports.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {reports.data.map((report) => (
            <ReportRow key={report.id} slug={slug} report={report} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// One report: the period under the figure, the channel and units in the meta line. The Owner's
// figure is the expected amount with settlement, paid and outstanding beside it; Staff get no
// money fields from the server, so their figure is the units.
function ReportRow({ slug, report }: { slug: string; report: SalesReport }) {
  const { t } = useTranslation()
  const money = report.expected_amount !== undefined
  const units = t('salesReports.units', { count: report.total_units })

  return (
    <RecordListItem
      voided={Boolean(report.voided_at)}
      title={
        <RecordLink to="/w/$slug/sales-reports/$reportId" params={{ slug, reportId: String(report.id) }}>
          {report.sales_report_no}
        </RecordLink>
      }
      badges={
        <>
          <StatusBadge report={report} />
          <VoidedBadge record={report} />
        </>
      }
      meta={[
        { icon: StoreIcon, label: t('salesReports.columns.channel'), value: report.sales_channel.name },
        money ? { icon: PackageIcon, label: t('salesReports.columns.units'), value: units } : null,
        money
          ? {
              icon: BanknoteIcon,
              label: t('salesReports.columns.settlement'),
              value: t('salesReports.list.settlement', { amount: formatMoney(report.settlement_amount ?? 0) }),
            }
          : null,
        money
          ? {
              icon: HandCoinsIcon,
              label: t('salesReports.columns.paid'),
              value: t('salesReports.list.paid', { amount: formatMoney(report.paid_amount ?? 0) }),
            }
          : null,
        money
          ? {
              icon: HourglassIcon,
              label: t('salesReports.columns.outstanding'),
              value: t('salesReports.list.outstanding', { amount: formatMoney(report.outstanding_amount ?? 0) }),
            }
          : null,
      ]}
      figure={money ? `Rp ${formatMoney(report.expected_amount ?? 0)}` : units}
      caption={t('salesReports.period', {
        from: formatBusinessDate(report.period_start),
        to: formatBusinessDate(report.period_end),
      })}
    />
  )
}
